import React, { Fragment } from "react";
import classnames from "classnames";
import { useGlobal } from "reactn";
import YellowAlert from "components/YellowAlert";
import MediaQuery from "components/MediaQuery";

import { thousandCondense, numberWithCommas } from "lib/general-helper.js";
import { getTierColor, getLuTierColor, getTier, getTierListPickRateCutoff, getLuTier } from "lib/tierHelper";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { useTranslation } from "react-i18next";

const ChampionRankingStats = (props) => {
  const { getChampionImg, getChampionIdByName, getChampionName, useChampionMini } = getRiotAssetsContext();
  const { rankingStats, oppChampionOverview, opp, queueType, missingMatchup, renderSpecialStats } = props;

  const { t } = useTranslation();
  const [language] = useGlobal("language");
  const { data: championMini } = useChampionMini({ skip: !opp, ssr: true, language });

  const {
    wins,
    matches,
    rank,
    total_rank,
    avg_damage,
    avg_gold,
    avg_kda,
    avg_cs,
    win_rate,
    pick_rate,
    ban_rate,
    total_matches,
    stdevs,
  } = rankingStats || {};

  let tierLetter = isNaN(stdevs) || !rank ? "?" : getLuTier(stdevs);

  // Values to display
  let winRate = !isNaN(win_rate) ? win_rate : "?";
  const oppId = opp && getChampionIdByName(opp);
  let championMatches = !isNaN(matches) ? numberWithCommas(matches) : "?";

  if (oppId) {
    winRate = !isNaN(oppChampionOverview.win_rate) ? 100 - oppChampionOverview.win_rate : "?";
    if (missingMatchup) {
      championMatches = 0;
    } else {
      championMatches = !isNaN(oppChampionOverview.matches) ? numberWithCommas(oppChampionOverview.matches) : "?";
    }
  }

  const tier = !isNaN(winRate) ? getTier(winRate) : "?";
  const tierColor = !isNaN(winRate) ? getTierColor(winRate) : "";
  const overallRank = rank && total_rank ? `${rank} / ${total_rank}` : "? / ?";
  const pickRate = !isNaN(pick_rate) && pick_rate !== null ? pick_rate.toFixed(1) : "?";
  const banRate = !isNaN(ban_rate) && ban_rate !== null ? ban_rate.toFixed(1) : "?";
  const avgDamage = !isNaN(avg_damage) ? thousandCondense(avg_damage) : "?";
  const avgCS = !isNaN(avg_cs) ? numberWithCommas(avg_cs) : "?";
  const kda = avg_kda !== null ? avg_kda : "?";

  let showWarning = pickRate === "?" || (pickRate !== "?" && pickRate < getTierListPickRateCutoff()) || championMatches < 100;
  if (queueType === "one_for_all") {
    showWarning = false;
  }
  let collapseState = renderSpecialStats ? "champion-ranking-stats-collapsed" : "champion-ranking-stats-normal";
  const classNames = classnames(`${collapseState}`, {
    "matchup-view": oppId,
  });
  let matchesStyle = oppId ? "matches-oppid" : "matches";

  return (
    <div className={classNames}>
      {!oppId && (
        <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
          <div className="champion-tier">
            <div className={`tier value ${getLuTierColor(stdevs)}`}>{tierLetter}</div>
            <div className="label">Tier</div>
          </div>
        </MediaQuery>
      )}
      <div className={`win-rate ${tierColor}`}>
        <div className="value">{`${winRate}%`}</div>
        <div className="label">
          {t("Win Rate")}
          {showWarning && <YellowAlert id={"win_rate"} label={"Low sample size"} />}
        </div>
      </div>
      {!oppId && (
        <Fragment>
          <div className="overall-rank">
            <div className="value">{overallRank}</div>
            <div className="label">{t("Rank")}</div>
          </div>
          <div className="pick-rate">
            <div className="value">{pickRate !== "?" && pickRate < 0.1 ? "< 0.1%" : `${pickRate}%`}</div>
            <div className="label">{t("Pick Rate")}</div>
          </div>
          <div className="ban-rate">
            <div className="value">{banRate !== "?" && banRate < 0.1 ? "-" : `${banRate}%`}</div>
            <div className="label">{t("Ban Rate")}</div>
          </div>
        </Fragment>
      )}
      <div className={matchesStyle}>
        <div className="value">{championMatches}</div>
        <div className="label">
          {t("Matches")}
          {oppId && missingMatchup && (
            <YellowAlert
              id={"matches"}
              label={
                "This matchup has too few games played this patch, so we are defaulting to our recommended build against all champions"
              }
            />
          )}
        </div>
      </div>
      {oppId && (
        <div className="opp-champion">
          <span>VS.</span>
          <img src={getChampionImg(oppId)} />
          <span>{getChampionName(oppId, { optionalData: championMini })}</span>
        </div>
      )}
    </div>
  );
};

export default ChampionRankingStats;
